import { useEffect } from 'react';
import { DeckNavBar } from './components';
import { useDeckNavigation } from './hooks';
import './MirrormerePitchDeck.css';

const DECK_TITLE = 'e76 Mirrormere';
const TOTAL_SLIDES = 8;

export default function MirrormerePitchDeck() {
  const {
    currentSlide,
    nextSlide,
    prevSlide,
    progress,
    isFirstSlide,
    isLastSlide,
  } = useDeckNavigation({ totalSlides: TOTAL_SLIDES });

  useEffect(() => {
    const previousTitle = document.title;
    document.title = DECK_TITLE;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="mirrormere-deck">
      <div className="deck-container">
        <DeckNavBar
          logoText="e76 MIRRORMERE"
          currentSlide={currentSlide}
          totalSlides={TOTAL_SLIDES}
          variant="minimal"
        />

        <div
          className="slides-wrapper"
          style={{ transform: `translateX(-${currentSlide * 100}vw)` }}
        >
          {/* Slide 1: Hero */}
          <div className="slide hero">
            <div className="hero-badge">Enterprise Intelligence</div>
            <h1 className="slide-title">
              Complete Organizational<br /><span className="emphasis">Awareness</span>
            </h1>
            <p className="slide-subtitle">
              A living model of your organization—every entity, relationship, process, and decision unified into a single intelligence layer.
            </p>
            <div className="hero-stats">
              <div className="hero-stat">
                <span className="hero-stat-value">$3T</span>
                <span className="hero-stat-label">Annual Cost of Bad Data (US)</span>
              </div>
              <div className="hero-stat">
                <span className="hero-stat-value">68%</span>
                <span className="hero-stat-label">Cite Fragmented Data as #1 Concern</span>
              </div>
              <div className="hero-stat">
                <span className="hero-stat-value">12hrs</span>
                <span className="hero-stat-label">Weekly Lost Searching for Information</span>
              </div>
            </div>
          </div>

          {/* Slide 2: The Problem */}
          <div className="slide">
            <span className="slide-label">The Problem</span>
            <h2 className="slide-title">
              Data Accumulates.<br /><span className="emphasis">Understanding Doesn't.</span>
            </h2>
            <p className="slide-subtitle">
              Enterprises generate more data than at any point in history—and understand less of it than ever before.
            </p>

            <div className="stat-row">
              <div className="stat-box">
                <div className="stat-box-value">900</div>
                <div className="stat-box-label">Average enterprise applications</div>
              </div>
              <div className="stat-box">
                <div className="stat-box-value">29%</div>
                <div className="stat-box-label">Actually integrated</div>
              </div>
              <div className="stat-box">
                <div className="stat-box-value">95%</div>
                <div className="stat-box-label">Hindered by data silos</div>
              </div>
              <div className="stat-box">
                <div className="stat-box-value">$25M</div>
                <div className="stat-box-label">Annual productivity loss per 1,000 employees</div>
              </div>
            </div>

            <div className="cards-grid two-col">
              <div className="card">
                <div className="card-icon">&#128269;</div>
                <h3 className="card-title">Information Never Found</h3>
                <p className="card-text">Data captured in one department never reaches another. Decisions made without complete context produce errors.</p>
              </div>
              <div className="card">
                <div className="card-icon">&#128200;</div>
                <h3 className="card-title">Patterns Undetected</h3>
                <p className="card-text">The insights that would transform operations remain invisible—not because they don't exist, but because they can't be connected.</p>
              </div>
              <div className="card">
                <div className="card-icon">&#9203;</div>
                <h3 className="card-title">Analysis Takes Weeks</h3>
                <p className="card-text">By the time insights arrive, conditions have changed. Analysis becomes historical record rather than decision support.</p>
              </div>
              <div className="card">
                <div className="card-icon">&#128279;</div>
                <h3 className="card-title">Causation Untraceable</h3>
                <p className="card-text">Decisions that caused damage are months in the past. The causal chain has become impossible to reconstruct.</p>
              </div>
            </div>
          </div>

          {/* Slide 3: Why Solutions Have Failed */}
          <div className="slide">
            <span className="slide-label">The Failure</span>
            <h2 className="slide-title">
              Traditional BI<br /><span className="emphasis">Was Never Enough</span>
            </h2>
            <p className="slide-subtitle">
              Engineered for batch processing, fixed semantic layers, and human-authored dashboards. These systems report what occurred—never why, or what comes next.
            </p>

            <div className="stat-row">
              <div className="stat-box">
                <div className="stat-box-value">$12.9M</div>
                <div className="stat-box-label">Annual cost of bad data per organization</div>
              </div>
              <div className="stat-box">
                <div className="stat-box-value">&lt;1%</div>
                <div className="stat-box-label">Unstructured data actually analyzed</div>
              </div>
              <div className="stat-box">
                <div className="stat-box-value">44%</div>
                <div className="stat-box-label">Time wasted on unsuccessful data prep</div>
              </div>
            </div>

            <div className="highlight-box">
              <div className="highlight-title">The architecture is broken at its foundation.</div>
              <div className="highlight-text">
                When different functions measure the same phenomena using incompatible methodologies, leadership receives contradictory reports about the same reality. Trust erodes. Decision-making slows.
              </div>
            </div>
          </div>

          {/* Slide 4: The Shift */}
          <div className="slide">
            <span className="slide-label">The Shift</span>
            <h2 className="slide-title">
              The Technology<br /><span className="emphasis">Has Changed</span>
            </h2>
            <p className="slide-subtitle">
              The technological substrate now permits capabilities that were impossible five years ago.
            </p>

            <div className="feature-list">
              <div className="feature-item">
                <div className="feature-icon">&#128172;</div>
                <div className="feature-content">
                  <div className="feature-title">Semantic Understanding</div>
                  <div className="feature-text">Systems can now comprehend meaning rather than match keywords. Context and nuance are no longer lost.</div>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">&#128101;</div>
                <div className="feature-content">
                  <div className="feature-title">Entity Resolution</div>
                  <div className="feature-text">Identify the same person, company, or product across disparate systems with precision that was previously impossible.</div>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">&#128221;</div>
                <div className="feature-content">
                  <div className="feature-title">Natural Language Interfaces</div>
                  <div className="feature-text">The barrier between question and answer has been eliminated. No SQL. No dashboards. Just answers.</div>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">&#129504;</div>
                <div className="feature-content">
                  <div className="feature-title">AI-Scale Processing</div>
                  <div className="feature-text">Process, connect, and reason across information volumes in ways that humans simply cannot.</div>
                </div>
              </div>
            </div>
          </div>

          {/* Slide 5: Introducing Mirrormere */}
          <div className="slide">
            <span className="slide-label">The Solution</span>
            <h2 className="slide-title">
              Introducing<br /><span className="emphasis">e76 Mirrormere</span>
            </h2>
            <p className="slide-subtitle">
              A living model of your organization. Everything it is, has accomplished, and might become.
            </p>

            <div className="cards-grid">
              <div className="card">
                <div className="card-icon">&#127919;</div>
                <h3 className="card-title">Unified Intelligence Layer</h3>
                <p className="card-text">Every entity, relationship, process, and decision unified into a single source of truth.</p>
              </div>
              <div className="card">
                <div className="card-icon">&#128218;</div>
                <h3 className="card-title">Institutional Memory</h3>
                <p className="card-text">Captures the reasoning behind decisions—context, trade-offs, alternatives rejected—independent of personnel.</p>
              </div>
              <div className="card">
                <div className="card-icon">&#128268;</div>
                <h3 className="card-title">Total Integration</h3>
                <p className="card-text">Every internal system feeds in—communication, operations, finance, documents—alongside external signals.</p>
              </div>
              <div className="card">
                <div className="card-icon">&#128270;</div>
                <h3 className="card-title">Pattern Detection</h3>
                <p className="card-text">Identifies patterns no human could detect across the volume and complexity of enterprise data.</p>
              </div>
              <div className="card">
                <div className="card-icon">&#9888;&#65039;</div>
                <h3 className="card-title">Risk Surfacing</h3>
                <p className="card-text">Surfaces risks and opportunities before they become visible through any other means.</p>
              </div>
              <div className="card">
                <div className="card-icon">&#128302;</div>
                <h3 className="card-title">Time Travel</h3>
                <p className="card-text">Reconstruct any historical state. Determine what would have occurred under alternative choices.</p>
              </div>
            </div>
          </div>

          {/* Slide 6: Capabilities */}
          <div className="slide">
            <span className="slide-label">Capabilities</span>
            <h2 className="slide-title">
              Analysis Beyond<br /><span className="emphasis">Traditional Platforms</span>
            </h2>
            <p className="slide-subtitle">
              Mirrormere comprehends meaning, resolves ambiguity, infers relationships, and identifies emerging conditions.
            </p>

            <div className="capability-grid">
              <div className="capability-card featured">
                <div className="capability-icon">&#128161;</div>
                <div className="capability-title">Deep Understanding</div>
                <div className="capability-text">
                  Maps how work actually flows through the organization. Detects where value is created and where it is lost. Performs root cause analysis on failures that would otherwise remain unexplained.
                </div>
              </div>
              <div className="capability-card">
                <div className="capability-icon">&#128202;</div>
                <div className="capability-title">Exposure Assessment</div>
                <div className="capability-text">
                  Assesses exposure across every dimension—operational, financial, relational, regulatory. Identifies vulnerabilities before they become crises.
                </div>
              </div>
              <div className="capability-card">
                <div className="capability-icon">&#128640;</div>
                <div className="capability-title">Future Modeling</div>
                <div className="capability-text">
                  Tests multiple futures under different assumptions. Stress-tests extreme conditions. Compares strategic alternatives with evidence-based modeling.
                </div>
              </div>
              <div className="capability-card">
                <div className="capability-icon">&#128187;</div>
                <div className="capability-title">Universal Access</div>
                <div className="capability-text">
                  Natural language, dynamic visualization, structured reporting, real-time dashboards. Every modality your organization requires.
                </div>
              </div>
              <div className="capability-card">
                <div className="capability-icon">&#128100;</div>
                <div className="capability-title">Role-Based Intelligence</div>
                <div className="capability-text">
                  Executives receive strategic summaries. Analysts get deep exploratory interfaces. Operators get contextual notifications.
                </div>
              </div>
            </div>
          </div>

          {/* Slide 7: Current Status */}
          <div className="slide">
            <span className="slide-label">Status</span>
            <h2 className="slide-title">
              Private<br /><span className="emphasis">Closed Beta</span>
            </h2>
            <p className="slide-subtitle">
              Available to select e76 clients. The program remains deliberately constrained to ensure exceptional results and deep learning from each deployment.
            </p>

            <div className="info-grid">
              <div className="info-card">
                <div className="info-card-title">Participants</div>
                <div className="info-card-value">Select e76 clients with complex enterprise data environments</div>
              </div>
              <div className="info-card">
                <div className="info-card-title">Approach</div>
                <div className="info-card-value">Deliberate constraints ensure exceptional results and deep learning</div>
              </div>
              <div className="info-card">
                <div className="info-card-title">Results</div>
                <div className="info-card-value">Early outcomes have exceeded expectations across all deployments</div>
              </div>
            </div>

            <div className="highlight-box">
              <div className="highlight-title">What Organizations Report</div>
              <div className="highlight-text">
                Dramatic compression of time-to-insight. Emergence of patterns that had been entirely invisible. Decision-making clarity that transforms how leadership engages with operations.
              </div>
            </div>
          </div>

          {/* Slide 8: CTA */}
          <div className="slide cta">
            <span className="slide-label">Get Started</span>
            <h2 className="slide-title">
              Interested in<br /><span className="emphasis">Mirrormere?</span>
            </h2>
            <p className="slide-subtitle">
              Whether as a prospective user or investor, complete the form to receive updates and explore partnership opportunities.
            </p>

            <div className="status-badge">
              <span className="status-dot"></span>
              Private Beta Access Available
            </div>

            <div className="cta-tagline">
              e76. <span className="emphasis">Engineering Excellence.</span>
            </div>
          </div>
        </div>

        {/* Navigation Controls */}
        <div className="nav-controls">
          <button
            className="nav-btn"
            onClick={prevSlide}
            disabled={isFirstSlide}
            aria-label="Previous slide"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <button
            className="nav-btn"
            onClick={nextSlide}
            disabled={isLastSlide}
            aria-label="Next slide"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
          <div className="keyboard-hint">
            <span className="key">&#8592;</span>
            <span className="key">&#8594;</span>
          </div>
        </div>
      </div>
    </div>
  );
}
